import { Dialog } from "@headlessui/react";
import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Button } from "../components/ui/Button.jsx";
import { Input } from "../components/ui/Input.jsx";
import { Textarea } from "../components/ui/TextArea.jsx";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/Select.jsx";
import { updateLead } from "../services/leadService.js";
import { fetchFields } from "../services/dynamicFieldService.js";
import { fetchStages } from "../services/leadStageService.js";
import { useFormValidation } from "../hooks/useFormValidation.js";
import { VALIDATION_RULES } from "../utils/validation";
import { Label } from "../components/ui/Label.jsx";
import { getCurrentUser } from "../services/authService.js";
import { fetchUsersByRole } from "../services/teamService.js";
import { useToast } from "../hooks/useToast.js";
import { getFieldOptionId, buildFieldPayload } from "../utils/fieldMapping.js";

const editLeadValidation = {
  name: VALIDATION_RULES.full_name,
  email: {
    required: { value: true, message: "Email is required" },
    pattern: {
      regex: /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/,
      message: "Invalid email format",
    },
  },
  address: {
    required: false,
    minLength: { value: 10, message: "Address must be at least 10 characters" },
    maxLength: { value: 200, message: "Address must be under 200 characters" },
  },
  phone_number: VALIDATION_RULES.phone_required,
  assignedTo: { required: false },
};

export default function EditLeadModal({
  isOpen,
  setIsOpen,
  lead,
  onLeadUpdated,
  assignOnly = false,
  hideFields = [],
}) {
  const [loading, setLoading] = useState(false);
  const [reps, setReps] = useState([]);
  const [currentUser, setCurrentUser] = useState(null);
  const { toast } = useToast();

  // Dynamic fields
  const [leadSourceField, setLeadSourceField] = useState(null);
  const [selectedLeadSource, setSelectedLeadSource] = useState("");
  const [otherFields, setOtherFields] = useState([]);
  const [dynamicValues, setDynamicValues] = useState({});

  // Lead stages
  const [stages, setStages] = useState([]);
  const [selectedStage, setSelectedStage] = useState("");

  const shouldHide = (field) => {
    if (assignOnly) return field !== "assignedTo";
    return hideFields.includes(field);
  };

  useEffect(() => {
    (async () => {
      const res = await getCurrentUser();
      if (res.success) setCurrentUser(res.user);
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetchUsersByRole("sales_person", { page: 1, limit: 10 });
        setReps(res?.users || res?.data || []);
      } catch {
        setReps([]);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetchFields();
        const allFields = res.data || [];
        const sysField = allFields.find((f) => f.is_system);
        setLeadSourceField(sysField || null);
        setOtherFields(allFields.filter((f) => !f.is_system));
      } catch {
        setLeadSourceField(null);
        setOtherFields([]);
      }
    })();
  }, []);

  useEffect(() => {
    (async () => {
      try {
        const res = await fetchStages();
        setStages(res.data || []);
      } catch {
        setStages([]);
      }
    })();
  }, []);

  const getAssignOptions = () => {
    if (!currentUser) return [];
    if (currentUser.user_role === "business_admin") {
      return [
        { id: currentUser.id, name: "Self" },
        ...reps.map((rep) => ({ id: rep.id, name: rep.name })),
      ];
    }
    if (currentUser.user_role === "sales_person") {
      return [{ id: currentUser.id, name: "Self" }];
    }
    return [];
  };

  const {
    form: formData,
    errors,
    touched,
    handleChange,
    handleBlur,
    validateAll,
    setFieldValue,
    setFormValues,
  } = useFormValidation(
    { name: "", email: "", phone_number: "", address: "", assignedTo: "" },
    editLeadValidation
  );

  // Pre-fill form when lead data is available
  useEffect(() => {
    if (lead) {
      setFormValues({
        name: lead.name || "",
        email: lead.email || "",
        phone_number: lead.phone_number || "",
        address: lead.address || "",
        assignedTo: lead.agent_assigned || "",
      });

      setSelectedStage(lead.deal_status || "new");

      // Pre-select Lead Source
      if (leadSourceField) {
        const optId = getFieldOptionId(lead, leadSourceField.field_label);
        setSelectedLeadSource(optId || "");
      }

      // Pre-select other dynamic fields
      if (otherFields.length > 0) {
        const preselected = {};
        otherFields.forEach((field) => {
          const optId = getFieldOptionId(lead, field.field_label);
          if (optId) preselected[field.id] = optId;
        });
        setDynamicValues(preselected);
      }
    }
  }, [lead, leadSourceField, otherFields]);

  const handleDynamicFieldChange = (fieldId, optionId) => {
    setDynamicValues((prev) => ({ ...prev, [fieldId]: optionId }));
  };

  const handleSave = async () => {
    if (!assignOnly && !validateAll()) return;

    // Validate required fields
    if (!assignOnly) {
      if (leadSourceField && !selectedLeadSource) {
        toast({ title: "Lead Source Required", description: "Please select a Lead Source.", variant: "destructive" });
        return;
      }
      if (!selectedStage) {
        toast({ title: "Lead Stage Required", description: "Please select a Lead Stage.", variant: "destructive" });
        return;
      }
    }

    setLoading(true);

    try {
      let payload;
      if (assignOnly) {
        payload = { agent_assigned: formData.assignedTo || null };
      } else {
        // Dynamic field payload
        const dynamicPayload = {};
        if (leadSourceField && selectedLeadSource) {
          const opt = leadSourceField.options?.find((o) => o.id === selectedLeadSource);
          const fp = buildFieldPayload(leadSourceField.field_label, selectedLeadSource, opt?.option_label);
          if (fp) Object.assign(dynamicPayload, fp);
        }
        otherFields.forEach((field) => {
          const selectedOptionId = dynamicValues[field.id];
          if (selectedOptionId) {
            const opt = field.options?.find((o) => o.id === selectedOptionId);
            const fp = buildFieldPayload(field.field_label, selectedOptionId, opt?.option_label);
            if (fp) Object.assign(dynamicPayload, fp);
          }
        });

        payload = {
          name: formData.name,
          phone_number: formData.phone_number,
          email: formData.email || null,
          address: formData.address || null,
          agent_assigned: formData.assignedTo || null,
          deal_status: selectedStage,
          ...dynamicPayload,
        };
      }

      await updateLead(lead.id, payload);

      onLeadUpdated?.({
        id: lead.id,
        agent_assigned: formData.assignedTo || null,
        agent_name:
          getAssignOptions().find(
            (u) => String(u.id) === String(formData.assignedTo)
          )?.name || null,
      });
      setIsOpen(false);
    } catch (err) {
      toast({
        variant: "destructive",
        title: "Update failed",
        description: "Failed to update lead. Please try again.",
      });
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <Dialog
          static
          open={isOpen}
          onClose={() => setIsOpen(false)}
          className="fixed inset-0 z-50 flex flex-col justify-end"
        >
          <motion.div
            className="fixed inset-0 bg-black/50 backdrop-blur-xs"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ duration: 0.25 }}
            onClick={() => setIsOpen(false)}
          />

          <motion.div
            key="edit-lead-modal"
            initial={{ y: "100%" }}
            animate={{ y: 0 }}
            exit={{ y: "100%" }}
            transition={{ type: "spring", stiffness: 180, damping: 22, mass: 0.5 }}
            className="relative z-50 w-full bg-white rounded-t-3xl shadow-lg overflow-hidden pb-5"
          >
            <div
              className="bg-gray-400/70 absolute top-1 left-1/2 -translate-x-1/2 h-1.5 w-20 rounded-full cursor-pointer"
              onClick={() => setIsOpen(false)}
            />

            <div className="pt-6 pb-4 px-6 sm:px-10 max-h-[70vh] overflow-y-auto">
              <h2 className="text-lg font-semibold text-gray-800 text-center mb-4">
                {assignOnly ? "Assign Agent" : "Edit Lead"}
              </h2>

              <div className="space-y-2">
                {/* Name */}
                {!shouldHide("name") && (
                  <div>
                    <Label>Name *</Label>
                    <Input name="name" value={formData.name} onChange={handleChange} onBlur={handleBlur} placeholder="Lead name" className={`h-8 text-sm text-gray-700 ${errors.name ? "border-red-500" : ""}`} />
                    {touched.name && errors.name && <p className="text-red-500 text-xs mt-1">{errors.name}</p>}
                  </div>
                )}

                {/* Email */}
                {!shouldHide("email") && (
                  <div>
                    <Label>Email *</Label>
                    <Input name="email" type="email" value={formData.email} onChange={handleChange} onBlur={handleBlur} placeholder="Email" className={`h-8 text-sm text-gray-700 ${errors.email ? "border-red-500" : ""}`} />
                    {touched.email && errors.email && <p className="text-red-500 text-xs mt-1">{errors.email}</p>}
                  </div>
                )}

                {/* Phone */}
                {!shouldHide("phone_number") && (
                  <div>
                    <Label>Phone *</Label>
                    <Input name="phone_number" type="tel" value={formData.phone_number} onChange={handleChange} onBlur={handleBlur} placeholder="Phone" maxLength={10} className={`h-8 text-sm text-gray-700 ${errors.phone_number ? "border-red-500" : ""}`} />
                    {touched.phone_number && errors.phone_number && <p className="text-red-500 text-xs mt-1">{errors.phone_number}</p>}
                  </div>
                )}

                {/* Lead Source (REQUIRED) */}
                {!assignOnly && leadSourceField && (
                  <div>
                    <Label>Lead Source *</Label>
                    {leadSourceField.options?.length > 0 ? (
                      <Select value={selectedLeadSource} onValueChange={setSelectedLeadSource}>
                        <SelectTrigger className="h-8 text-sm text-gray-700">
                          <SelectValue placeholder="Select Lead Source" />
                        </SelectTrigger>
                        <SelectContent>
                          {leadSourceField.options.map((opt) => (
                            <SelectItem key={opt.id} value={opt.id}>{opt.option_label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <p className="text-[11px] text-red-400 mt-1">No options configured</p>
                    )}
                  </div>
                )}

                {/* Lead Stage (REQUIRED) */}
                {!assignOnly && stages.length > 0 && (
                  <div>
                    <Label>Lead Stage *</Label>
                    <Select value={selectedStage} onValueChange={setSelectedStage}>
                      <SelectTrigger className="h-8 text-sm text-gray-700">
                        <SelectValue placeholder="Select Stage" />
                      </SelectTrigger>
                      <SelectContent>
                        {stages.map((s) => (
                          <SelectItem key={s.id} value={s.stage_key}>{s.stage_label}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                )}

                {/* Other Dynamic Fields */}
                {!assignOnly && otherFields.map((field) => (
                  <div key={field.id}>
                    <Label>{field.field_label}</Label>
                    {field.options?.length > 0 ? (
                      <Select value={dynamicValues[field.id] || ""} onValueChange={(v) => handleDynamicFieldChange(field.id, v)}>
                        <SelectTrigger className="h-8 text-sm text-gray-700">
                          <SelectValue placeholder={`Select ${field.field_label}`} />
                        </SelectTrigger>
                        <SelectContent>
                          {field.options.map((opt) => (
                            <SelectItem key={opt.id} value={opt.id}>{opt.option_label}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    ) : (
                      <p className="text-[11px] text-gray-400 mt-1">No options available</p>
                    )}
                  </div>
                ))}

                {/* Address */}
                {!shouldHide("name") && (
                  <div>
                    <Label>Address</Label>
                    <Textarea name="address" value={formData.address} onChange={handleChange} onBlur={handleBlur} placeholder="Address" rows={3} className={`text-sm text-gray-700 ${errors.address ? "border-red-500" : ""}`} />
                    {touched.address && errors.address && <p className="text-red-500 text-xs mt-1">{errors.address}</p>}
                  </div>
                )}

                {/* Assign To */}
                {!shouldHide("assignedTo") && (
                  <div>
                    <Label>Re-assign To</Label>
                    {currentUser && (
                      <Select value={formData.assignedTo || ""} onValueChange={(v) => setFieldValue("assignedTo", v)}>
                        <SelectTrigger className="h-8 text-sm text-gray-700">
                          <SelectValue placeholder="Select Sales Person" />
                        </SelectTrigger>
                        <SelectContent>
                          {getAssignOptions().map((user) => (
                            <SelectItem key={user.id} value={String(user.id)} className="cursor-pointer">{user.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    )}
                  </div>
                )}
              </div>

              <div className="flex justify-end gap-2 mt-5">
                <Button variant="outline" onClick={() => setIsOpen(false)} className="h-8 px-4 text-xs rounded-full border">Cancel</Button>
                <Button onClick={handleSave} disabled={loading} className="h-8 px-4 text-xs rounded-full bg-gradient-to-r from-[#00a8b4fa] to-[#00c8d391] text-white border">
                  {loading ? "Updating..." : "Update"}
                </Button>
              </div>
            </div>
          </motion.div>
        </Dialog>
      )}
    </AnimatePresence>
  );
}
